import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachFibArc from "./wrapper/EachFibArc";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class FibonacciArcs extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawArcs = this.handleDrawArcs.bind(this);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType("arcs").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {
      current: null,
      override: null,
    };
  }

  handleDrawArcs(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleDrag(index, xy) {
    this.setState({
      override: {
        index,
        ...xy,
      },
    });
  }

  handlePoint1Drag(index, newXY) {
    const { arcs } = this.props;
    const item = arcs[index];

    this.setState({
      override: {
        index,
        x1: newXY[0],
        y1: newXY[1],
        x2: item.x2,
        y2: item.y2,
      },
    });
  }

  handlePoint2Drag(index, newXY) {
    const { arcs } = this.props;
    const item = arcs[index];

    this.setState({
      override: {
        index,
        x1: item.x1,
        y1: item.y1,
        x2: newXY[0],
        y2: newXY[1],
      },
    });
  }

  handleDragComplete(moreProps) {
    const { arcs } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newArcs = arcs.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newArcs, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps) {
    const { current } = this.state;
    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;
      this.setState(
        {
          current: {
            x1: xyValue[0],
            y1: xyValue[1],
            x2: null,
            y2: null,
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { arcs, appearance } = this.props;
    const { current } = this.state;

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const currentAppearance = appearance;

      const newArcs = arcs.concat({
        ...current,
        x2: xyValue[0],
        y2: xyValue[1],
        selected: true,
        appearance: currentAppearance,
      });

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newArcs, moreProps, e);
        },
      );
    }
  }

  render() {
    const { enabled, arcs, appearance, hoverText } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...FibonacciArcs.defaultProps.hoverText,
      ...hoverText,
    };

    const tempArc =
      isDefined(current) && isDefined(current.x1) ? (
        <EachFibArc
          interactive={false}
          {...current}
          appearance={appearance}
          hoverText={hoverTextWithDefaults}
        />
      ) : null;

    return (
      <g>
        {arcs.map((each, idx) => {
          // Deep merge appearance
          const eachAppearance = isDefined(each.appearance)
            ? {
                ...appearance,
                ...each.appearance,
                stroke:
                  typeof each.appearance.stroke === "string"
                    ? { ...appearance.stroke, color: each.appearance.stroke }
                    : typeof each.appearance.stroke === "object"
                      ? { ...appearance.stroke, ...each.appearance.stroke }
                      : appearance.stroke,
                fill:
                  typeof each.appearance.fill === "string"
                    ? { ...appearance.fill, color: each.appearance.fill }
                    : typeof each.appearance.fill === "object"
                      ? { ...appearance.fill, ...each.appearance.fill }
                      : appearance.fill,
                font:
                  typeof each.appearance.font === "string"
                    ? { ...appearance.font, color: each.appearance.font }
                    : typeof each.appearance.font === "object"
                      ? { ...appearance.font, ...each.appearance.font }
                      : appearance.font,
              }
            : appearance;

          const props = {
            key: idx,
            ref: this.saveNodeType(idx),
            index: idx,
            ...each,
            appearance: eachAppearance,
            hoverText: hoverTextWithDefaults,

            selected: each.selected,
            hovering: each.hovering,

            onDrag: this.handleDrag,
            onDragComplete: this.handleDragComplete,

            onPoint1Drag: this.handlePoint1Drag.bind(this, idx),
            onPoint2Drag: this.handlePoint2Drag.bind(this, idx),

            ...(isDefined(override) && override.index === idx ? override : {}),
          };

          return <EachFibArc {...props} />;
        })}
        {tempArc}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          shouldDisableSnap={() => true}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawArcs}
          onClick={this.handleEnd}
          r={4}
          stroke={appearance.stroke?.color || appearance.stroke || "#00BFFF"}
          strokeWidth={2}
          opacity={1}
          data-evo-id="interactive-fibonacci-arcs"
        />
      </g>
    );
  }
}

FibonacciArcs.propTypes = {
  enabled: PropTypes.bool.isRequired,
  arcs: PropTypes.array.isRequired,
  appearance: PropTypes.object.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object.isRequired,
};

FibonacciArcs.defaultProps = {
  arcs: [],
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
};

export default FibonacciArcs;
